"use client";

import Link from "next/link";
import LuxeNavLink, { LUXE_NAV_LINK_LIVE, isLuxeNavActive } from "@/components/luxe/LuxeNavLink";
import AccountHeaderLinkAnonShell from "./AccountHeaderLinkAnonShell";
import AccountHeaderLinkAuth from "./AccountHeaderLinkAuth";
import Image from "next/image";
import { useState, useEffect, useRef, useCallback, useMemo, useSyncExternalStore } from "react";
import { isInAppBrowser } from "@/lib/detect-inapp-browser";
import { useRouter, usePathname } from "next/navigation";
import IngLogo from "@/components/brand/IngLogo";
import NavAanbiedingenMenu, { aanbiedingenNaKlik } from "./NavAanbiedingenMenu";
import { useSiteContext } from "@/components/providers/SiteContextProvider";
import {
  LANGUAGE_OPTIONS,
  LANG_COOKIE,
  LANG_QUERY,
  applyMarketPrefix,
  stripLocalePrefix,
} from "@/lib/site-routing";
import { isFarTravelMarket } from "@/lib/far-travel-market";
import { exclusiveToegestaan } from "@/lib/luxe/luxe-markt";
import { exclusiveAccountReturnTo } from "@/lib/luxe/luxe-return-to";
import { luxeDeInlogHref } from "@/lib/luxe/luxe-de-publiek";
import { formatPriceText } from "@/lib/format-price";
import { convertEurTo } from "@/lib/currency-convert";
import { getTranslations } from "@/lib/i18n";
import { titleCaseLabel } from "@/lib/title-case";
import { getLocalizedField } from "@/lib/localized-field";
import { getLocalizedCategoryName, getLocalizedDestinationLabel } from "@/lib/category-display";
import { splitMatches, tokensFromQuery } from "@/lib/highlight-match";
import { localizeHref as localizeCatalogHref } from "@/lib/slug-paths";
import { categoryPath } from "@/lib/category-path";
import { redeemHrefForMarket, isRedeemPath } from "@/lib/redeem-paths";
import { isFilmKopRoute, isDetailFilmKopRoute } from "@/lib/filmkop-route";
import { useGeenFilmKop } from "@/lib/filmkop-signaal";

// Zoekpaneel dat het Luxe-vergrootglas (LP14) opent. Het id staat alleen op
// het paneel als dat vergrootglas er is, zodat de Navbar met de Luxe-knop uit
// gelijk blijft aan main.
export const LUXE_NAV_SEARCH_PANEL_ID = "nav-search-panel";

const AANBIEDINGEN_KEY = "aanbiedingen";
const AANBIEDINGEN_PANEL_ID = "nav-aanbiedingen-panel";

// Vanaf deze scrollstand is de kopbalk op de homepage weer wit.
const HOME_SCROLL_DREMPEL_PX = 40;

const BELGIAN_FLAG = "\u{1F1E7}\u{1F1EA}";

// 2026-04-22 booking.com-style language switcher: the header dropdown no
// longer redirects across domains. Click a language → write the cookie +
// reload the current URL with ?ft-lang=xx so the middleware strips the
// param and the next render picks the new locale.
function persistLanguagePreference(locale) {
  if (typeof window === "undefined") return;
  try {
    window.document.cookie = `${LANG_COOKIE}=${locale}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;
  } catch {
    // Cookie write blocked (Safari ITP strict-mode incognito, etc.). The
    // ?ft-lang=xx fallback below still works: middleware writes the cookie
    // server-side from that param.
  }
}

// --- Rich nav dropdown helpers (demo-style: icon + subtitle) ---
// Inline outline icons matching the static prototype's mega-menu.
export function navIcon(key) {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    className: "w-5 h-5 flex-shrink-0",
  };
  switch (key) {
    case "wellness":
      return (<svg {...common}><path d="m12 3 1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" /><path d="M19 15l.9 2.1L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.9z" /></svg>);
    case "stedentrip":
      return (<svg {...common}><path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" /></svg>);
    case "pretpark":
      return (<svg {...common}><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z" /><path d="M13 5v14" /></svg>);
    case "kasteel":
      return (<svg {...common}><path d="M22 20V9l-3 1V6l-3 1V4l-4 2-4-2v3L5 6v4L2 9v11" /><path d="M2 20h20M9 20v-4a3 3 0 0 1 6 0v4" /></svg>);
    case "culinair":
      return (<svg {...common}><path d="M3 2v7c0 1.1.9 2 2 2h0a2 2 0 0 0 2-2V2M5 2v20M16 2v20M21 7c0 3-2 4-5 4 0-2 0-9 5-9z" /></svg>);
    case "natuur":
      return (<svg {...common}><path d="m8 3 4 8 5-5 5 11H2L8 3z" /></svg>);
    case "zomer":
      return (<svg {...common}><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" /></svg>);
    case "rondreizen":
      return (<svg {...common}><circle cx="6" cy="5" r="3" /><circle cx="18" cy="19" r="3" /><path d="M9 5h8a4 4 0 0 1 0 8H7a3 3 0 0 0 0 6h8" /></svg>);
    case "oud-en-nieuw":
      return (<svg {...common}><path d="M12 3v3M5 5l3 3M3 12h3M5 19l3-3M21 12h-3M19 5l-3 3M19 19l-3-3M12 11v10" /></svg>);
    case "kerstmarkten":
      return (<svg {...common}><path d="m12 2-5 6h3l-5 6h4l-5 5h16l-5-5h4l-5-6h3L12 2zM12 19v3" /></svg>);
    case "pin":
      return (<svg {...common}><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" /><circle cx="12" cy="10" r="3" /></svg>);
    case "verre-reizen":
      return (<svg {...common}><path d="M21.54 15H17a2 2 0 0 0-2 2v4.54" /><path d="M7 3.34V5a3 3 0 0 0 3 3a2 2 0 0 1 2 2c0 1.1.9 2 2 2a2 2 0 0 0 2-2c0-1.1.9-2 2-2h3.17" /><path d="M11 21.95V18a2 2 0 0 0-2-2a2 2 0 0 1-2-2v-1a2 2 0 0 0-2-2H2.05" /><circle cx="12" cy="12" r="10" /></svg>);
    case "voucher":
      return (<svg {...common}><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z" /><path d="M13 5v14" /></svg>);
    case "gift":
      return (<svg {...common}><rect x="3" y="8" width="18" height="4" rx="1" /><path d="M12 8v13M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" /><path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8M16.5 8a2.5 2.5 0 0 0 0-5C13 3 12 8 12 8" /></svg>);
    case "users":
      return (<svg {...common}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>);
    case "shield":
      return (<svg {...common}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" /></svg>);
    case "check":
      return (<svg {...common}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="m9 11 3 3L22 4" /></svg>);
    default:
      return (<svg {...common}><circle cx="12" cy="12" r="9" /></svg>);
  }
}

export function categoryIconKey(name) {
  const n = (name || "").toLowerCase();
  // Exact canonical slugs and display labels; never match loose "oud"/"nieuw".
  // Keep the known category list in Navbar.category-icons.test.jsx in sync
  // whenever a category goes live (mapping + implemented SVG are both guarded).
  const slug = n.trim().replace(/&/g, " en ").replace(/[\s_-]+/g, "-");
  if (slug === "rondreizen") return "rondreizen";
  if (slug === "oud-en-nieuw") return "oud-en-nieuw";
  if (slug === "kerstmarkten") return "kerstmarkten";
  if (n.includes("wellness") || n.includes("spa")) return "wellness";
  // Backend slug for city trips is "city_trip" (no "sted"), so match both the
  // slug and the Dutch display name.
  if (n.includes("sted") || n.includes("city") || n.includes("citytrip")) return "stedentrip";
  if (n.includes("pretpark") || n.includes("themapark") || n.includes("attractie")) return "pretpark";
  if (n.includes("kasteel") || n.includes("castle")) return "kasteel";
  if (n.includes("culinair") || n.includes("eten") || n.includes("drinken") || n.includes("proeverij") || n.includes("food")) return "culinair";
  if (n.includes("natuur") || n.includes("wandel") || n.includes("outdoor")) return "natuur";
  // Summer holidays: slug "zomervakantie", display "Zomervakantie". Kept
  // narrow ("zomer"/"strand"/"summer") to avoid false hits like "horizon".
  if (n.includes("zomer") || n.includes("strand") || n.includes("summer")) return "zomer";
  return "default";
}

// NL-only curated destination subtitles (cities + countries). Non-NL markets
// get icon + name only — no translated-from-Dutch subtitle leak.
const DEST_SUB_NL = {
  parijs: "Stad van de liefde",
  berlijn: "Bruisende hoofdstad",
  dusseldorf: "Shoppen aan de Rijn",
  düsseldorf: "Shoppen aan de Rijn",
  veluwe: "Natuur dichtbij huis",
  ardennen: "Wandelen en weldaad",
  moezel: "Wijn en kastelen",
  nederland: "Dichtbij huis",
  belgie: "Steden en kust",
  belgië: "Steden en kust",
  duitsland: "Steden en natuur",
  frankrijk: "Van kust tot platteland",
  engeland: "Londen en meer",
  oostenrijk: "Bergen en alpenmeren",
  zweden: "Meren en wilde natuur",
  spanje: "Zon, zee en cultuur",
};
export function destinationSubtitle(name, locale) {
  if (locale !== "nl") return "";
  const n = (name || "").toLowerCase();
  for (const k of Object.keys(DEST_SUB_NL)) if (n.includes(k)) return DEST_SUB_NL[k];
  return "";
}

const VOUCHER_SUB = {
  nl: { voucher: "Een beleving als cadeau", gift: "Met persoonlijke boodschap", users: "Cadeaus voor je team", shield: "Altijd de volledige waarde", check: "Voucher inwisselen" },
  pl: { voucher: "Podaruj wyjątkowe przeżycie", gift: "Z osobistą wiadomością", users: "Prezenty dla twojego zespołu", shield: "Zawsze pełna wartość", check: "Zrealizuj voucher" },
  de: { voucher: "Ein Erlebnis verschenken", gift: "Mit persönlicher Botschaft", users: "Geschenke für Ihr Team", shield: "Immer der volle Wert", check: "Gutschein einlösen" },
  fr: { voucher: "Offrir une expérience", gift: "Avec un message personnel", users: "Cadeaux pour votre équipe", shield: "Toujours la valeur totale", check: "Utiliser un bon" },
  en: { voucher: "Gift an experience", gift: "With a personal message", users: "Gifts for your team", shield: "Always the full value", check: "Redeem a voucher" },
};

// Zelfde patroon als InAppBrowserBanner.js: de user agent verandert niet na mount.
const emptySubscribe = () => () => {};
const serverInAppBrowser = () => false;

export default function Navbar({ categories = [], destinations = [], journeyAvailable = false, filmKop = true }) {
  const { shortLabel, currency, locale, effectiveRate, key: marketKey, domain } = useSiteContext();
  const toCurrency = (eur) => convertEurTo(eur, currency, effectiveRate);
  const { t } = getTranslations(locale);
  // Exclusive-menulink en vergrootglas: favotrip.nl (nl-nl), en met
  // NEXT_PUBLIC_LUXE_BE ook favotrip.be in het Nederlands (luxe-markt.js).
  const luxeNavHier = LUXE_NAV_LINK_LIVE && exclusiveToegestaan(null, marketKey, locale);
  const [mobileOpen, setMobileOpen] = useState(false);
  // Mobile-menu accordion: 1 section open at a time, "categories" by default
  // so the user lands on a populated state instead of a wall of headers.
  const [mobileExpandedSection, setMobileExpandedSection] = useState("categories");
  const [mobileMarketOpen, setMobileMarketOpen] = useState(false);
  const [marketMenuOpen, setMarketMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState({ results: [], vouchers: [], categories: [], didYouMean: [] });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  // Index into `flatSuggestions` for keyboard navigation. -1 = nothing
  // active (Enter on input triggers free-form search submit).
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [aanbiedingenVast, setAanbiedingenVast] = useState(false);
  const router = useRouter();
  const pathname = usePathname();
  const normalizedPathname = stripLocalePrefix(pathname || "/");
  // Home-, categorie- en gewone dealpagina's: doorzichtig over de film zolang de
  // pagina bovenaan staat. `scrolled` begint false en de route is op de server
  // bekend, dus server en eerste browserweergave zijn gelijk (geen flits van
  // wit naar doorzichtig).
  // Op een dealpad meldt een weergave zonder filmkop (fout, niet gevonden) zich
  // via GeenFilmKop: dan blijft de balk wit. Standaard (en op de server) is er
  // wel een filmkop, dus de gewone dealpagina flitst niet.
  const geenFilmKop = useGeenFilmKop();
  const opFilmKop =
    isFilmKopRoute(normalizedPathname) &&
    !(geenFilmKop && isDetailFilmKopRoute(normalizedPathname)) &&
    // `filmKop={false}`: de pagina die deze Navbar zelf rendert (404) heeft geen
    // film, ook niet in de server-HTML (geen wachten op hydratatie).
    !(filmKop === false && isDetailFilmKopRoute(normalizedPathname));
  const [scrolled, setScrolled] = useState(false);
  // Op /exclusive/* en /luxe/*: de inloglinks in de kop dragen de terugweg
  // naar deze pagina (null op elke andere route: de link blijft gelijk).
  const accountReturnTo = exclusiveAccountReturnTo(normalizedPathname);
  // Op favotrip.de/exclusive* (LUXE_DE_PUBLIEK_LIVE): het Duitse aanmeldblok
  // op deze pagina in plaats van de Nederlandse Exclusive-login (fe#1937).
  const accountAanmeldHref = luxeDeInlogHref(domain, accountReturnTo);
  const searchRef = useRef(null);
  const dropdownTimeout = useRef(null);
  const searchDebounce = useRef(null);
  const marketMenuRef = useRef(null);
  const aanbiedingenTriggerRef = useRef(null);
  const activeLanguage =
    LANGUAGE_OPTIONS.find((opt) => opt.code === locale) || LANGUAGE_OPTIONS[0];
  // favotrip.be (nl-be, fr-be) verkoopt aan Belgen: de taal "Nederlands" toont
  // daar de Belgische vlag, niet de Nederlandse. Andere markten en talen
  // houden hun vlag uit LANGUAGE_OPTIONS.
  const isBelgianSite = domain === "favotrip.be" || marketKey === "nl-be" || marketKey === "fr-be";
  const flagFor = (option) => (isBelgianSite && option.code === "nl" ? BELGIAN_FLAG : option.flag);

  function localizeHref(href) {
    // Emit the market-localized catalog slug (e.g. /cadeaubon -> /gutschein on
    // .de, /vouchers -> /gutscheine) so nav links land on the canonical URL
    // without the middleware's 301 hop. Non-catalog paths (/packages,
    // /verzilveren which the redeem handler owns, external) pass through. The
    // physical Dutch route stays the render target via the middleware rewrite.
    return localizeCatalogHref(applyMarketPrefix(href), locale);
  }

  function persistLanguageChoice(targetLocale) {
    if (typeof window === "undefined") return;
    persistLanguagePreference(targetLocale);

    // 2026-04-22 Codex review medium-fix: switcher used to call
    // window.location.assign() which is a hard reload — booking wizard
    // Zustand state (non-persisted) and voucher-checkout form state
    // (local useState + react-hook-form) got wiped mid-flow.
    //
    // router.push() + router.refresh() does a soft client navigation,
    // preserves Zustand + form state, and still lets Next re-run the
    // server components with the new cookie + Accept-Language.
    //
    // Fire a polite a11y announcement so screen-reader users get told
    // the language changed even though the visible UI barely moves.
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set(LANG_QUERY, targetLocale);
    const target = nextUrl.pathname + nextUrl.search + nextUrl.hash;
    router.push(target);
    router.refresh();
    announceLanguageChange(targetLocale);
  }

  // a11y: dispatch a polite live-region announcement when the language
  // switcher fires. We keep a single off-screen <div aria-live="polite">
  // in the DOM (rendered below) and just mutate its text.
  function announceLanguageChange(targetLocale) {
    if (typeof document === "undefined") return;
    const option =
      LANGUAGE_OPTIONS.find((opt) => opt.code === targetLocale) ||
      LANGUAGE_OPTIONS[0];
    const el = document.getElementById("favotrip-lang-live-region");
    if (el) el.textContent = `Language changed to ${option.nativeLabel}`;
  }

  // Determine which nav section is active based on current path
  function isNavActive(key) {
    if (key === "vouchers") return normalizedPathname.startsWith("/vouchers");
    if (key === "overig") {
      // /over-ons is now localized (nl "/over-ons" vs. e.g. de "/ueber-uns"),
      // so compare against the market-localized href, not the raw nl slug —
      // otherwise this nav item stops highlighting on every non-NL/EN market.
      return ["/contact", localizeCatalogHref("/over-ons", locale), "/faq"].some((p) => normalizedPathname.startsWith(p));
    }
    const nav = navItems.find((item) => item.key === key);
    return nav?.items?.some((item) => normalizedPathname === stripLocalePrefix(item.href) || normalizedPathname.startsWith(`${stripLocalePrefix(item.href)}/`));
  }

  useEffect(() => {
    if (!opFilmKop) return undefined;
    const onScroll = () => setScrolled(window.scrollY >= HOME_SCROLL_DREMPEL_PX);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [opFilmKop]);

  // Close mobile menu on route change
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      setMobileOpen(false);
      setSearchOpen(false);
      setMobileMarketOpen(false);
      setMarketMenuOpen(false);
      setActiveDropdown(null);
      setMobileExpandedSection("categories");
    });

    return () => window.cancelAnimationFrame(frame);
  }, [pathname]);

  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (marketMenuRef.current && !marketMenuRef.current.contains(event.target)) {
        setMarketMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Prevent body scroll when mobile menu or language sheet is open
  useEffect(() => {
    if (mobileOpen || mobileMarketOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen, mobileMarketOpen]);

  const fetchSuggestions = useCallback((term) => {
    clearTimeout(searchDebounce.current);
    if (!term || term.length < 2) {
      setSuggestions({ results: [], vouchers: [], categories: [], didYouMean: [] });
      setShowSuggestions(false);
      setSearchLoading(false);
      setActiveSuggestionIndex(-1);
      return;
    }
    // Spinner appears immediately so the input doesn't feel dead
    // during the 300ms debounce + BFF round-trip.
    setSearchLoading(true);
    searchDebounce.current = setTimeout(async () => {
      try {
        // Forward the active locale so the BFF picks the matching
        // synonym map (paris↔parijs, wellness↔wohlfühl, etc.).
        const res = await fetch(
          `/api/deals/search?q=${encodeURIComponent(term)}&locale=${encodeURIComponent(locale || "nl")}`,
        );
        const data = await res.json();
        setSuggestions({ didYouMean: [], ...data });
        // E2E QA 2026-06-10: keep the dropdown OPEN on a successful search
        // with zero hits so we can render an explicit "Geen resultaten
        // gevonden" state instead of silently doing nothing.
        setShowSuggestions(true);
        setActiveSuggestionIndex(-1);
      } catch (_) {
        setShowSuggestions(false);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  }, [locale]);

  function handleSearchInput(event) {
    const value = event.target.value;
    setSearchTerm(value);
    fetchSuggestions(value);
  }

  function persistSearchQuery(query) {
    // V1.5 (2026-04-30): persist last-5 searches in `ft_searches` cookie so
    // the homepage SSR can boost matching deals in the popularity carousel.
    // Pipe-delimited, URL-encoded, case-insensitive on the read side.
    if (typeof document === "undefined") return;
    try {
      const trimmed = String(query || "").trim();
      if (!trimmed) return;
      const cookieMatch = document.cookie
        .split("; ")
        .find((c) => c.startsWith("ft_searches="));
      const existing = cookieMatch
        ? decodeURIComponent(cookieMatch.split("=").slice(1).join("="))
            .split("|")
            .map((s) => decodeURIComponent(s).trim())
            .filter(Boolean)
        : [];
      // De-dup case-insensitively, prepend new query, cap at 5.
      const lower = trimmed.toLowerCase();
      const filtered = existing.filter((q) => q.toLowerCase() !== lower);
      const updated = [trimmed, ...filtered].slice(0, 5);
      const encoded = updated.map((q) => encodeURIComponent(q)).join("|");
      document.cookie = `ft_searches=${encodeURIComponent(encoded)};max-age=${21 * 24 * 60 * 60};path=/;samesite=lax`;
    } catch {
      // Cookie write blocked — silent fail, fallback to popularity-only on next homepage SSR.
    }
  }

  function handleSearch(event) {
    event.preventDefault();
    const term = searchTerm.trim();
    if (!term) return;
    // 2026-05-20 perceived-responsiveness: previously router.push fired
    // BEFORE the modal-close + input-clear state writes. Next.js wraps
    // router.push in a React transition, so those state updates were
    // batched until the new route's RSC came back ~1-3s later — the
    // search dropdown sat open with the typed term visible for the
    // full navigation. Flip the order: close + clear instantly so the
    // visitor sees their submit register in the same frame as the
    // NavProgressBar top-bar.
    setSearchOpen(false);
    setSearchTerm("");
    setSuggestions({ results: [], vouchers: [], categories: [], didYouMean: [] });
    setShowSuggestions(false);
    persistSearchQuery(term);
    // Lowercase: category/destination URLs are canonically lowercase
    // (middleware 4b 301s mixed-case); typing "Parijs" should land on
    // /packages/parijs directly, not via a redirect hop.
    router.push(localizeHref(categoryPath(term, { lowercase: true })));
  }

  function handleSuggestionClick(href) {
    // Same instant-feedback flip as handleSearch above — close + clear
    // before the navigation so the dropdown collapses on click instead
    // of after the RSC fetch resolves.
    setSearchOpen(false);
    setSearchTerm("");
    setSuggestions({ results: [], vouchers: [], categories: [], didYouMean: [] });
    setShowSuggestions(false);
    router.push(localizeHref(stripLocalePrefix(href)));
  }

  // "Bedoelde je <chip>" — re-runs the search with the corrected
  // term instead of navigating. Keeps the dropdown open so the user
  // sees the corrected results immediately.
  function handleDidYouMeanClick(term) {
    setSearchTerm(term);
    setActiveSuggestionIndex(-1);
    fetchSuggestions(term);
  }

  // Flat traversal list for ↑/↓/Enter keyboard navigation. Order
  // matches visual render order so screen-readers + sighted-keyboard
  // users see the same sequence.
  const flatSuggestions = useMemo(() => {
    const out = [];
    for (const c of suggestions.categories || []) {
      out.push({ kind: "category", href: c.href, label: c.name });
    }
    for (const d of suggestions.results || []) {
      out.push({ kind: "deal", href: d.href, label: d.title });
    }
    for (const v of suggestions.vouchers || []) {
      out.push({ kind: "voucher", href: v.href, label: v.title });
    }
    for (const s of suggestions.didYouMean || []) {
      out.push({ kind: "didYouMean", term: s.term, label: s.term });
    }
    return out;
  }, [suggestions]);

  // Match-highlight tokens — split the typed query into the same
  // tokens the BFF index used, so we mark the same substrings.
  const matchTokens = useMemo(
    () => tokensFromQuery(searchTerm),
    [searchTerm],
  );

  function renderHighlighted(text) {
    const parts = splitMatches(text, matchTokens);
    return parts.map((p, i) =>
      p.isMatch ? (
        <mark key={i} className="bg-yellow-100 text-inherit rounded-sm px-0.5">
          {p.text}
        </mark>
      ) : (
        <span key={i}>{p.text}</span>
      ),
    );
  }

  function handleSearchKeyDown(event) {
    // Only intercept when the dropdown is showing real navigable rows.
    if (!showSuggestions || flatSuggestions.length === 0) {
      if (event.key === "Escape") {
        setShowSuggestions(false);
      }
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveSuggestionIndex((i) =>
        i + 1 >= flatSuggestions.length ? 0 : i + 1,
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveSuggestionIndex((i) =>
        i <= 0 ? flatSuggestions.length - 1 : i - 1,
      );
    } else if (event.key === "Enter") {
      if (activeSuggestionIndex >= 0 && activeSuggestionIndex < flatSuggestions.length) {
        event.preventDefault();
        const target = flatSuggestions[activeSuggestionIndex];
        if (target.kind === "didYouMean") {
          handleDidYouMeanClick(target.term);
        } else if (target.href) {
          handleSuggestionClick(target.href);
        }
      }
      // else: fall through to the form's onSubmit (free-form search)
    } else if (event.key === "Escape") {
      event.preventDefault();
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
    } else if (event.key === "Home") {
      event.preventDefault();
      setActiveSuggestionIndex(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setActiveSuggestionIndex(flatSuggestions.length - 1);
    }
  }

  // INTL bug-batch 2026-06-10 items 2+3: the old per-word `\b\w` regex
  // broke accented labels ("StäDtetrips") and the label was never
  // localized, so NL categories like "Zomervakantie" leaked into the nav
  // on .de/.fr/.co.uk. Resolve the CMS i18n slot first, then the central
  // category map, then locale-aware title-casing.
  function titleCase(value) {
    return titleCaseLabel(value, locale);
  }

  function localizedCategoryLabel(category) {
    return titleCaseLabel(
      getLocalizedField(category, "name", locale) ||
        getLocalizedField(category, "display_name", locale) ||
        getLocalizedCategoryName(
          category.display_name || category.category_name,
          locale,
        ),
      locale,
    );
  }

  function handleMouseEnter(key) {
    clearTimeout(dropdownTimeout.current);
    setActiveDropdown(key);
  }

  const aanbiedingenOpen = activeDropdown === AANBIEDINGEN_KEY;

  function handleMouseLeave() {
    // Een met een klik vastgezet Aanbiedingen-paneel blijft open als de muis weggaat.
    if (aanbiedingenOpen && aanbiedingenVast) return;
    dropdownTimeout.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  }

  function handleAanbiedingenClick() {
    clearTimeout(dropdownTimeout.current);
    const next = aanbiedingenNaKlik({ open: aanbiedingenOpen, vast: aanbiedingenVast });
    setActiveDropdown(next.open ? AANBIEDINGEN_KEY : null);
    setAanbiedingenVast(next.vast);
  }

  // De gedeelde hover-timeout mag na unmount niet meer afgaan.
  useEffect(() => {
    return () => clearTimeout(dropdownTimeout.current);
  }, []);

  const closeAanbiedingen = useCallback(() => {
    clearTimeout(dropdownTimeout.current);
    setActiveDropdown(null);
  }, []);

  // Elke keer dat het paneel dichtgaat (tweede klik, Escape, klik buiten,
  // routewissel, ander menu) vervalt het vastzetten.
  useEffect(() => {
    if (!aanbiedingenOpen) setAanbiedingenVast(false);
  }, [aanbiedingenOpen]);

  // In de browser van een app (Instagram, Facebook, TikTok) staat de sticky
  // InAppBrowserBanner boven de pagina: de hero begint dan niet bovenaan en
  // onder de kopbalk zou een witte strook met een wit logo komen. Server en
  // eerste weergave geven false, dus de HTML blijft gelijk.
  const inAppBrowser = useSyncExternalStore(emptySubscribe, isInAppBrowser, serverInAppBrowser);

  // Doorzichtige stand: alleen filmkop-routes, bovenaan, en geen enkel menu open.
  const onHero =
    opFilmKop &&
    !inAppBrowser &&
    !scrolled &&
    !mobileOpen &&
    !searchOpen &&
    !marketMenuOpen &&
    !mobileMarketOpen &&
    activeDropdown === null;
  const heroTekst = onHero ? { color: "#ffffff" } : null;
  // Lichte schaduw onder witte tekst en pictogrammen, alleen in de doorzichtige stand.
  const heroSchaduw = onHero ? " [text-shadow:0_1px_3px_rgba(0,0,0,0.55)] [&_svg]:drop-shadow-[0_1px_3px_rgba(0,0,0,0.55)]" : "";

  const navItems = [
    {
      label: t("nav.reiscategorie"),
      key: "categories",
      items: [
        ...(journeyAvailable && isFarTravelMarket({ key: marketKey, locale }) ? [{ label: getLocalizedCategoryName("Verre Reizen", locale), href: "/verre-reizen", iconKey: "verre-reizen" }] : []),
        ...categories.filter(category => category.category_name?.toLowerCase() !== "verreizen").map((category) => ({
        label: localizedCategoryLabel(category),
        // Canonical category/destination URLs are lowercase (middleware 4b
        // 301s mixed-case); lowercase at the source so menu links don't pay
        // an avoidable redirect hop.
        // Rondreizen woont op /rondreizen (/packages/rondreizen is een 301). Op
        // .de wijst het menu direct naar /rundreisen; elders blijft de link
        // zoals hij was.
        href:
          locale === "de" && (category.category_name || "").toLowerCase() === "rondreizen"
            ? localizeHref("/rondreizen")
            : localizeHref(`/packages/${encodeURIComponent((category.category_name || "").toLowerCase())}`),
        iconKey: categoryIconKey(category.category_name || category.display_name),
      })),
      ],
    },
    {
      label: t("nav.reisbestemming"),
      key: "destinations",
      items: destinations.map((destination) => ({
        label: titleCase(getLocalizedDestinationLabel(destination.name, locale)),
        href: localizeHref(`/packages/${encodeURIComponent((destination.name || "").toLowerCase())}`),
        iconKey: "pin",
        subtitle: destinationSubtitle(destination.name, locale),
      })),
    },
  ];

  // Vouchers & cadeau dropdown items (prototype IA). Static links — no backend
  // data needed. Each target page exists (/vouchers, /cadeaubon, /zakelijk,
  // /vouchergarantie, /verzilveren).
  const vsub = VOUCHER_SUB[locale] || VOUCHER_SUB.nl;
  const voucherMenu = [
    { label: t("nav.voucher_kopen"), href: localizeHref("/vouchers"), iconKey: "voucher", subtitle: vsub.voucher },
    { label: t("nav.cadeaubon"), href: localizeHref("/cadeaubon"), iconKey: "gift", subtitle: vsub.gift },
    { label: t("nav.zakelijk"), href: localizeHref("/zakelijk"), iconKey: "users", subtitle: vsub.users },
    { label: t("nav.vouchergarantie"), href: localizeHref("/vouchergarantie"), iconKey: "shield", subtitle: vsub.shield },
    // /verzilveren is a MARKET-keyed redeem slug (/wymien on .pl, /einloesen on
    // .de, …), not a catalog slug — localizeHref would leave it Dutch. Resolve
    // it directly from the shared redeem map so the link lands on the canonical
    // per-market slug without the middleware's 301 hop.
    { label: t("nav.verzilveren"), href: redeemHrefForMarket(marketKey), iconKey: "check", subtitle: vsub.check },
  ];

  return (
    <header>
      {/* a11y live region for the language switcher — Codex review medium-fix
          2026-04-22: visual selected-state doesn't reach assistive tech
          strongly enough. Switcher writes into this region via
          announceLanguageChange(). Kept visually hidden. */}
      <div
        id="favotrip-lang-live-region"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      />
      <nav
        data-filmkop-kopbalk={onHero ? "" : undefined}
        className={
          opFilmKop
            ? `${onHero ? "bg-transparent" : "bg-white"} fixed top-0 left-0 w-full z-50 transition-colors duration-200`
            : "bg-white fixed top-0 left-0 w-full z-50"
        }
        style={{ borderBottom: onHero ? "0.5px solid transparent" : "0.5px solid rgb(233, 233, 235)" }}
      >
        {opFilmKop && (
          // Donker (0.6 -> 0.4) tot onder de tekstrij: 62% = ~80px (kleiner scherm, rij tot ~60px)
          // en ~105px (xl, rij tot ~95px); daarna zacht naar 0.
          <div
            aria-hidden="true"
            data-testid="nav-home-verloop"
            data-filmkop-verloop=""
            className={`pointer-events-none absolute inset-x-0 top-0 h-[130px] xl:h-[170px] transition-opacity duration-200 ${onHero ? "opacity-100" : "opacity-0"}`}
            style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.4) 62%, rgba(0,0,0,0) 100%)" }}
          />
        )}
        {/* Het logo houdt in elke stand dezelfde layoutmaat (184x45), zodat de
            menulinks niet schuiven als de film-stand wisselt (anders flikkert
            het menu bij hoveren). Bovenaan de homepage wordt het vanaf xl met
            een transform vergroot (84/45), die de layout niet raakt. De
            className van ouder en logo is in elke stand gelijk (Navbar.kop-
            320px-be-vlag.test.jsx); alleen het data-attribuut wisselt. */}
        <div
          data-onhero={onHero ? "" : undefined}
          className="relative max-w-[1320px] mx-auto px-4 xl:data-[onhero]:[&>div>a:first-child>img]:scale-[1.8667] xl:data-[onhero]:[&>div>a:first-child>img]:origin-left"
        >
          <div className="flex items-center justify-between py-3 xl:py-[30px] max-sm:gap-2">
            <Link
              href={(() => {
                if (normalizedPathname.startsWith("/booking/")) {
                  const slug = normalizedPathname.replace("/booking/deals/", "").replace("/booking/", "");
                  const query = typeof window !== "undefined" ? window.location.search : "";
                  return localizeHref(`/packages/deals/${slug}${query}`);
                }
                if (normalizedPathname.startsWith("/packages/deals/")) return localizeHref("/");
                if (normalizedPathname.startsWith("/vouchers/") && normalizedPathname !== "/vouchers") return localizeHref("/vouchers");
                return localizeHref("/");
              })()}
              className="flex-shrink-0 max-sm:min-w-0 max-sm:flex-shrink focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0A8DE9] focus-visible:ring-offset-2 rounded-sm"
            >
              <Image
                data-filmkop-logo=""
                src={onHero ? "/images/favotrip-logo-hero.svg" : "/images/favotrip-logo-v2.svg"}
                alt="Favotrip"
                width={184}
                height={45}
                priority
                className="h-[45px] w-auto max-sm:max-w-full max-sm:object-contain max-sm:object-left"
                style={onHero ? { filter: "drop-shadow(0 1px 3px rgba(0,0,0,.45))" } : undefined}
              />
            </Link>

            <div className={`hidden xl:flex items-center gap-0${onHero ? " [&>a]:!text-white" : ""}${heroSchaduw}`}>
              {/* re-skin 2026-06-02: prototype nav adds a top-level "Aanbiedingen" link */}
              {/* Rustige kopbalk (26-10-2026): Aanbiedingen is een knop die het
                  brede uitklappaneel (NavAanbiedingenMenu, onder de <nav>) opent
                  met Soort reis, Bestemmingen en Ook handig. */}
              <button
                type="button"
                ref={aanbiedingenTriggerRef}
                onClick={handleAanbiedingenClick}
                onMouseEnter={() => handleMouseEnter(AANBIEDINGEN_KEY)}
                onMouseLeave={handleMouseLeave}
                aria-expanded={aanbiedingenOpen}
                aria-controls={AANBIEDINGEN_PANEL_ID}
                className="inline-flex min-h-[44px] items-center gap-1 px-2 whitespace-nowrap text-[16px] font-semibold transition-colors hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A8DE9]"
                style={heroTekst || { color: "#1a1b2e" }}
              >
                {t("nav.aanbiedingen")}
                <svg
                  className={`h-4 w-4 transition-transform ${aanbiedingenOpen ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
              {/* Luxe-ledenomgeving (LP10): favotrip.nl (nl-nl), met NEXT_PUBLIC_LUXE_BE ook nl-be; uit te zetten via LUXE_NAV_LINK_LIVE */}
              {luxeNavHier && <LuxeNavLink active={isLuxeNavActive(normalizedPathname)} />}

              {/* Vouchers & cadeau — dropdown (prototype IA): the trigger links
                  to /vouchers; hover reveals cadeaubon/zakelijk/garantie/verzilveren. */}
              <div
                className="relative"
                onMouseEnter={() => handleMouseEnter("vouchers")}
                onMouseLeave={handleMouseLeave}
              >
                <Link
                  href={localizeHref("/vouchers")}
                  className="inline-flex min-h-[44px] items-center px-2 whitespace-nowrap text-[16px] font-semibold transition-colors hover:text-blue-600"
                  style={heroTekst || (isNavActive("vouchers") ? { color: "#0A8DE9" } : { color: "#1a1b2e" })}
                >
                  {t("nav.vouchers")}
                </Link>
                {activeDropdown === "vouchers" && (
                  <div className="absolute top-full left-0 mt-1 w-[320px] bg-white rounded-xl shadow-xl border border-gray-100 p-2 z-50">
                    {voucherMenu.map((item) => {
                      // WP6c reviewronde 2: the verzilveren item is a hard
                      // <a>, not next/link's <Link> — a soft SPA navigation
                      // here would skip layout.js's boot-time
                      // tracking-sensitive-path guard (see isRedeemPath's
                      // own docblock in redeem-paths.js).
                      const ItemTag = isRedeemPath(item.href) ? "a" : Link;
                      return (
                        <ItemTag
                          key={item.href}
                          href={item.href}
                          className="flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-blue-50"
                        >
                          <span className="text-[#0A8DE9]">{navIcon(item.iconKey)}</span>
                          <span className="flex min-w-0 flex-col leading-tight">
                            <span className="text-[15px] font-semibold text-gray-900">{item.label}</span>
                            {item.subtitle ? (
                              <span className="text-[12px] text-gray-500">{item.subtitle}</span>
                            ) : null}
                          </span>
                        </ItemTag>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className={`hidden xl:flex items-center gap-1${onHero ? " [&>button]:!text-white" : ""}${heroSchaduw}`}>
              {/* De ING-knop staat sinds 26-10-2026 in het Aanbiedingen-paneel
                  (kolom "Ook handig"), met dezelfde nl-nl-grens op de marktsleutel. */}
              <div className="relative" ref={marketMenuRef}>
                <button
                  type="button"
                  onClick={() => setMarketMenuOpen((open) => !open)}
                  data-testid="desktop-taalknop"
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-medium transition-colors ${onHero ? "border-white/60 text-white hover:border-white" : "border-gray-200 text-gray-700 hover:border-blue-200 hover:text-blue-600"}`}
                >
                  <span>{flagFor(activeLanguage)} {activeLanguage.shortLabel}</span>
                  <svg className={`h-4 w-4 transition-transform ${marketMenuOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="m6 9 6 6 6-6" /></svg>
                </button>

                {marketMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 rounded-2xl border border-gray-200 bg-white p-2 shadow-xl">
                    <p className="px-3 pb-2 pt-1 text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">
                      {t("nav.taal")}
                    </p>
                    {LANGUAGE_OPTIONS.map((option) => (
                      <button
                        key={option.code}
                        type="button"
                        onClick={() => persistLanguageChoice(option.code)}
                        className={`flex w-full items-center justify-between rounded-xl px-3 py-3 text-left transition-colors ${option.code === locale ? "bg-blue-50 text-blue-700" : "hover:bg-gray-50 text-gray-700"}`}
                      >
                        <span className="flex items-center gap-3">
                          <span className="text-lg leading-none" aria-hidden="true">{flagFor(option)}</span>
                          <span className="block text-sm font-semibold">{option.nativeLabel}</span>
                        </span>
                        <span className="text-xs font-semibold uppercase tracking-[0.14em]">{option.shortLabel}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Bug 2 fix: SSR-renderable shell + client-side auth swap.
                  The shell renders the anonymous "Inloggen" pill server-side
                  on marketing-layout pages (which static-generate and would
                  otherwise drop the hooks-using Client Component from SSR).
                  AccountHeaderLinkAuth hydrates on the client and toggles
                  the wrapper's data-account-header-mode so CSS hides
                  whichever variant is not active. */}
              <span
                className={`account-header-link-wrap${onHero ? " [&_a]:!border-white/60 [&_a]:!bg-transparent [&_a]:!text-white" : ""}`}
                data-account-header-wrap="desktop"
                data-account-header-mode="anon"
              >
                <AccountHeaderLinkAnonShell variant="desktop" locale={locale} returnTo={accountReturnTo} aanmeldHref={accountAanmeldHref} />
                <AccountHeaderLinkAuth variant="desktop" />
              </span>

              {/* Luxe-menulink (LP14): staat de link aan, dan is "Zoek aanbieding"
                  op desktop alleen een vergrootglas, anders past de rij niet op
                  1280 px. Dezelfde voorwaarde als de link zelf, dus met
                  LUXE_NAV_LINK_LIVE uit rendert hier exact de knop van voorheen. */}
              {luxeNavHier ? (
                <button
                  type="button"
                  onClick={() => setSearchOpen(!searchOpen)}
                  data-testid="nav-search-icon"
                  aria-label={t("nav.zoek_aanbieding")}
                  title={t("nav.zoek_aanbieding")}
                  aria-expanded={searchOpen}
                  aria-controls={searchOpen ? LUXE_NAV_SEARCH_PANEL_ID : undefined}
                  className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-black/65 transition-colors hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A8DE9]"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
                </button>
              ) : (
                <button
                  onClick={() => setSearchOpen(!searchOpen)}
                  className="whitespace-nowrap text-[16px] transition-colors hover:text-blue-600 min-h-[40px]"
                  style={{ color: "rgba(0,0,0,0.65)" }}
                >
                  {t("nav.zoek_aanbieding")}
                </button>
              )}
            </div>

            <div className={`flex flex-shrink-0 items-center gap-1 sm:gap-2 xl:hidden${heroSchaduw}`}>
              <button
                type="button"
                onClick={() => setMobileMarketOpen(true)}
                className={`hidden sm:inline-flex rounded-full border px-3 py-2 text-xs font-semibold ${onHero ? "border-white/60 text-white" : "border-gray-200 text-gray-700"}`}
                aria-label={t("nav.mobile_market_aria")}
              >
                {flagFor(activeLanguage)} {activeLanguage.shortLabel}
              </button>
              <span
                className={`account-header-link-wrap${onHero ? " [&_a]:!text-white" : ""}`}
                data-account-header-wrap="mobile"
                data-account-header-mode="anon"
              >
                <AccountHeaderLinkAnonShell variant="mobile" locale={locale} returnTo={accountReturnTo} aanmeldHref={accountAanmeldHref} />
                <AccountHeaderLinkAuth variant="mobile" />
              </span>

              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className={`p-2 min-w-[40px] min-h-[40px] flex items-center justify-center ${onHero ? "text-white" : "text-gray-500"}`}
                aria-label={t("nav.mobile_search_aria")}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
              </button>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className={`p-2 flex-shrink-0 min-w-[40px] min-h-[40px] flex items-center justify-center ${onHero ? "text-white" : "text-gray-500"}`}
                aria-label={t("nav.mobile_menu_aria")}
              >
                {mobileOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" /></svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 12h18M3 6h18M3 18h18" /></svg>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="hidden xl:block">
          <NavAanbiedingenMenu
            open={aanbiedingenOpen}
            id={AANBIEDINGEN_PANEL_ID}
            titels={{
              soortReis: t("nav.menu_soort_reis"),
              bestemmingen: t("nav.reisbestemming"),
              handig: t("nav.menu_ook_handig"),
            }}
            soortReis={(navItems.find((nav) => nav.key === "categories")?.items ?? []).map((item) => ({ ...item, icon: navIcon(item.iconKey) }))}
            bestemmingen={(navItems.find((nav) => nav.key === "destinations")?.items ?? []).map((item) => ({ ...item, icon: navIcon(item.iconKey) }))}
            handig={[
              { label: `${t("nav.menu_alle_aanbiedingen")} →`, href: localizeHref("/packages/deals") },
              { label: t("nav.inspiratie"), href: localizeHref("/inspiratie") },
            ]}
            marketKey={marketKey}
            ingHref={localizeHref("/ing")}
            onClose={closeAanbiedingen}
            triggerRef={aanbiedingenTriggerRef}
            onMouseEnter={() => handleMouseEnter(AANBIEDINGEN_KEY)}
            onMouseLeave={handleMouseLeave}
          />
        </div>

        {searchOpen && (() => {
          // Pre-compute per-section offsets so each row knows its
          // `activeSuggestionIndex` slot without us threading a
          // mutable counter through the JSX.
          const catCount = suggestions.categories?.length || 0;
          const dealCount = suggestions.results?.length || 0;
          const voucherCount = suggestions.vouchers?.length || 0;
          const dymCount = suggestions.didYouMean?.length || 0;
          const dealOffset = catCount;
          const voucherOffset = catCount + dealCount;
          const dymOffset = catCount + dealCount + voucherCount;
          const dymOnly = catCount === 0 && dealCount === 0 && voucherCount === 0;
          const activeId =
            activeSuggestionIndex >= 0 &&
            activeSuggestionIndex < flatSuggestions.length
              ? `search-option-${activeSuggestionIndex}`
              : undefined;
          const activeRowClass = "bg-blue-50 ring-1 ring-inset ring-blue-200";
          return (
            <div
              className="border-t bg-white px-4 py-3"
              id={luxeNavHier ? LUXE_NAV_SEARCH_PANEL_ID : undefined}
            >
              <div className="max-w-2xl mx-auto relative">
                <form onSubmit={handleSearch} className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      ref={searchRef}
                      type="text"
                      value={searchTerm}
                      onChange={handleSearchInput}
                      onKeyDown={handleSearchKeyDown}
                      onFocus={() => {
                        if (searchTerm.trim().length >= 2) {
                          setShowSuggestions(true);
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => setShowSuggestions(false), 200);
                      }}
                      placeholder={t("nav.zoek_placeholder")}
                      role="combobox"
                      aria-autocomplete="list"
                      aria-expanded={showSuggestions}
                      aria-controls="favotrip-search-listbox"
                      aria-activedescendant={activeId}
                      aria-busy={searchLoading || undefined}
                      autoComplete="off"
                      className="w-full px-4 py-2 pr-9 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                    {searchLoading && (
                      <span
                        className="absolute right-3 top-1/2 -translate-y-1/2 inline-block h-4 w-4 rounded-full border-2 border-blue-200 border-t-blue-600 animate-spin"
                        aria-hidden="true"
                        data-testid="search-loading-spinner"
                      />
                    )}
                  </div>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                  >
                    {t("nav.zoeken")}
                  </button>
                </form>

                {showSuggestions && (
                  <div
                    id="favotrip-search-listbox"
                    role="listbox"
                    aria-label={t("nav.zoek_placeholder")}
                    className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto"
                  >
                    {catCount > 0 && (
                      <div className="px-3 py-2 border-b">
                        <p className="text-xs font-semibold text-gray-400 uppercase mb-1">{t("nav.categorieen")}</p>
                        {suggestions.categories.map((category, i) => {
                          const idx = i;
                          const isActive = idx === activeSuggestionIndex;
                          return (
                            <button
                              key={category.href}
                              id={`search-option-${idx}`}
                              role="option"
                              aria-selected={isActive}
                              onMouseDown={() => handleSuggestionClick(category.href)}
                              onMouseEnter={() => setActiveSuggestionIndex(idx)}
                              className={`block w-full text-left px-2 py-1.5 text-sm text-gray-700 rounded ${isActive ? activeRowClass : "hover:bg-gray-50"}`}
                            >
                              {renderHighlighted(category.name)}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {dealCount > 0 && (
                      <div className="px-3 py-2">
                        <p className="text-xs font-semibold text-gray-400 uppercase mb-1">{t("nav.deals")}</p>
                        {suggestions.results.map((deal, i) => {
                          const idx = dealOffset + i;
                          const isActive = idx === activeSuggestionIndex;
                          return (
                            <button
                              key={deal.id}
                              id={`search-option-${idx}`}
                              role="option"
                              aria-selected={isActive}
                              onMouseDown={() => handleSuggestionClick(deal.href)}
                              onMouseEnter={() => setActiveSuggestionIndex(idx)}
                              className={`flex items-center gap-3 w-full text-left px-2 py-2 rounded ${isActive ? activeRowClass : "hover:bg-gray-50"}`}
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{renderHighlighted(deal.title)}</p>
                                <p className="text-xs text-gray-500">{deal.category}</p>
                              </div>
                              {deal.price && (
                                <span className="flex flex-col items-end flex-shrink-0">
                                  <span className="text-sm font-semibold text-orange-600">
                                    {formatPriceText(toCurrency(deal.price), { currency, locale })}
                                  </span>
                                  {/* Owner rule 2026-08-15 (feedback_voucher_prijs_per_twee_personen_communiceren.md):
                                      deal rows and voucher rows sit next to
                                      each other in this same dropdown --
                                      exactly the case the rule flags as
                                      needing the per-person unit on the
                                      direct-trip price too, reusing DealCard's
                                      existing deal_card.per_person key. */}
                                  <span className="text-[10px] text-gray-400 leading-tight">{t("deal_card.per_person")}</span>
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {voucherCount > 0 && (
                      <div className="px-3 py-2 border-t">
                        <p className="text-xs font-semibold text-gray-400 uppercase mb-1">{t("nav.vouchers")}</p>
                        {suggestions.vouchers.map((voucher, i) => {
                          const idx = voucherOffset + i;
                          const isActive = idx === activeSuggestionIndex;
                          return (
                            <button
                              key={voucher.id}
                              id={`search-option-${idx}`}
                              role="option"
                              aria-selected={isActive}
                              onMouseDown={() => handleSuggestionClick(voucher.href)}
                              onMouseEnter={() => setActiveSuggestionIndex(idx)}
                              className={`flex items-center gap-3 w-full text-left px-2 py-2 rounded ${isActive ? activeRowClass : "hover:bg-gray-50"}`}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium text-gray-900 truncate">{renderHighlighted(voucher.title)}</p>
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700 flex-shrink-0">
                                    {t("nav.voucher_badge")}
                                  </span>
                                </div>
                              </div>
                              {voucher.price && (
                                <span className="flex flex-col items-end flex-shrink-0">
                                  <span className="text-sm font-semibold text-orange-600">
                                    {formatPriceText(toCurrency(voucher.price), { currency, locale })}
                                  </span>
                                  {/* Owner rule 2026-08-15 (feedback_voucher_prijs_per_twee_personen_communiceren.md):
                                      a voucher is always ONE price for TWO
                                      people -- must be explicit, reusing
                                      DealCard's existing
                                      deal_card.per_2_persons key. */}
                                  <span className="text-[10px] text-gray-400 leading-tight">{t("deal_card.per_2_persons")}</span>
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {catCount === 0 && dealCount === 0 && voucherCount === 0 && dymCount === 0 && !searchLoading && (
                      <div
                        className="px-3 py-4 text-sm text-gray-500"
                        data-testid="search-no-results"
                        role="status"
                      >
                        {t("nav.geen_resultaten")}
                      </div>
                    )}
                    {dymCount > 0 && dymOnly && (
                      <div className="px-3 py-3" data-testid="did-you-mean">
                        <p className="text-xs font-semibold text-gray-400 uppercase mb-2">
                          {t("nav.bedoelde_je") || "Bedoelde je"}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {suggestions.didYouMean.map((s, i) => {
                            const idx = dymOffset + i;
                            const isActive = idx === activeSuggestionIndex;
                            return (
                              <button
                                key={s.term}
                                id={`search-option-${idx}`}
                                role="option"
                                aria-selected={isActive}
                                onMouseDown={() => handleDidYouMeanClick(s.term)}
                                onMouseEnter={() => setActiveSuggestionIndex(idx)}
                                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${isActive ? "bg-blue-200 text-blue-900" : "bg-blue-50 text-blue-700 hover:bg-blue-100"}`}
                              >
                                {s.term}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {mobileOpen && (
          <div className="xl:hidden border-t bg-white max-h-[80vh] overflow-y-auto">
            <ul className="divide-y divide-gray-200" data-testid="mobile-nav-accordion">
              {/* re-skin 2026-06-02: prototype mobile nav leads with Aanbiedingen */}
              <li>
                <Link
                  href={localizeHref("/packages/deals")}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center px-4 py-4 text-base font-bold text-gray-900 hover:text-blue-600"
                  style={{ minHeight: "56px" }}
                >
                  {t("nav.aanbiedingen")}
                </Link>
              </li>
              {luxeNavHier && (
                <li>
                  <LuxeNavLink variant="mobile" active={isLuxeNavActive(normalizedPathname)} onClick={() => setMobileOpen(false)} />
                </li>
              )}
              {navItems.map((nav) =>
                nav.items.length > 0 ? (
                  <MobileAccordionSection
                    key={nav.key}
                    sectionKey={nav.key}
                    label={nav.label}
                    items={nav.items}
                    expanded={mobileExpandedSection === nav.key}
                    onToggle={() =>
                      setMobileExpandedSection((current) =>
                        current === nav.key ? null : nav.key,
                      )
                    }
                    onItemClick={() => setMobileOpen(false)}
                  />
                ) : null,
              )}

              {/* Vouchers & cadeau — accordion (prototype IA): voucher kopen /
                  cadeaubon / zakelijk / vouchergarantie / verzilveren */}
              <MobileAccordionSection
                sectionKey="vouchers"
                label={t("nav.vouchers")}
                items={voucherMenu}
                expanded={mobileExpandedSection === "vouchers"}
                onToggle={() =>
                  setMobileExpandedSection((current) =>
                    current === "vouchers" ? null : "vouchers",
                  )
                }
                onItemClick={() => setMobileOpen(false)}
              />

              {/* Inspiratie — top-level link */}
              <li>
                <Link
                  href={localizeHref("/inspiratie")}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center px-4 py-4 text-base font-bold text-gray-900 hover:text-blue-600"
                  style={{ minHeight: "56px" }}
                >
                  {t("nav.inspiratie")}
                </Link>
              </li>

              {/* ING reference — favotrip.nl (nl-nl) market ONLY (see desktop note) */}
              {marketKey === "nl-nl" && (
                <li>
                  <Link
                    href={localizeHref("/ing")}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 px-4 py-4 text-base font-bold text-gray-900 hover:text-blue-600"
                    style={{ minHeight: "56px" }}
                  >
                    <IngLogo height={20} />
                    ING-vragen en aanbiedingen
                  </Link>
                </li>
              )}
            </ul>

            <div className="px-4 py-4">
              <button
                type="button"
                onClick={() => setMobileMarketOpen(true)}
                data-testid="mobile-menu-taalregel"
                className="flex w-full items-center justify-between rounded-2xl border border-gray-200 px-4 py-3 text-left"
              >
                <span>
                  <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">{t("nav.taal")}</span>
                  <span className="block text-sm font-semibold text-gray-900">
                    {flagFor(activeLanguage)} {activeLanguage.nativeLabel}
                  </span>
                </span>
                <span className="text-xs font-semibold uppercase">{activeLanguage.shortLabel}</span>
              </button>
            </div>
          </div>
        )}
      </nav>

      {mobileMarketOpen && (
        <div className="fixed inset-0 z-[70] bg-black/30 xl:hidden" onClick={() => setMobileMarketOpen(false)}>
          <div
            className="absolute inset-x-0 bottom-0 rounded-t-[28px] bg-white px-4 pb-8 pt-4 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mx-auto mb-4 h-1.5 w-14 rounded-full bg-gray-200" />
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">Favotrip</p>
                <h2 className="mt-1 text-xl font-semibold text-gray-900">{t("nav.kies_taal")}</h2>
              </div>
              <button type="button" onClick={() => setMobileMarketOpen(false)} className="rounded-full border border-gray-200 p-2 text-gray-500">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18 18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="space-y-2">
              {LANGUAGE_OPTIONS.map((option) => (
                <button
                  key={option.code}
                  type="button"
                  onClick={() => persistLanguageChoice(option.code)}
                  className={`flex w-full items-center justify-between rounded-2xl border px-4 py-4 text-left transition-colors ${option.code === locale ? "border-blue-200 bg-blue-50 text-blue-700" : "border-gray-200 text-gray-700"}`}
                >
                  <span className="flex items-center gap-3">
                    <span className="text-lg leading-none" aria-hidden="true">{flagFor(option)}</span>
                    <span className="block text-sm font-semibold">{option.nativeLabel}</span>
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-[0.14em]">{option.shortLabel}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function MobileAccordionSection({
  sectionKey,
  label,
  items,
  expanded,
  onToggle,
  onItemClick,
}) {
  const panelId = `mobile-nav-panel-${sectionKey}`;
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={panelId}
        data-testid={`mobile-nav-toggle-${sectionKey}`}
        className="flex w-full items-center justify-between px-4 py-4 text-left hover:bg-gray-50 transition-colors"
        style={{ minHeight: "56px" }}
      >
        <span className="text-base font-bold text-gray-900 tracking-wide">
          {label}
        </span>
        <svg
          className={`h-5 w-5 text-gray-700 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {expanded && (
        <div
          id={panelId}
          data-testid={`mobile-nav-panel-${sectionKey}`}
          className="bg-gray-50 pb-2"
        >
          {items.map((item) => {
            // WP6c reviewronde 2: same hard-<a> requirement as the desktop
            // dropdown above — this section also renders voucherMenu
            // (which contains the /verzilveren item) on mobile.
            const ItemTag = isRedeemPath(item.href) ? "a" : Link;
            return (
              <ItemTag
                key={item.href}
                href={item.href}
                onClick={onItemClick}
                className="flex items-center px-8 py-3 text-sm text-gray-700 hover:text-blue-600"
                style={{ minHeight: "44px" }}
              >
                {item.label}
              </ItemTag>
            );
          })}
        </div>
      )}
    </li>
  );
}
